import type { Backend } from '@freelancer/datastore/core';
import {
  getQueryParamValue,
  OrderByDirection,
} from '@freelancer/datastore/core';
import { isArray, isDefined } from '@freelancer/utils';
import type { SearchActiveLoadsCollection } from './search-active-loads.types';

export function searchActiveLoadsBackend(): Backend<SearchActiveLoadsCollection> {
  return {
    defaultOrder: {
      field: 'id',
      direction: OrderByDirection.DESC,
    }, // This endpoint is only used with search queries so doesn't need an ordering

    fetch: (authUid, ids, query, order) => {
      // Get pickup date range
      const pickupDateRange = {
        begin: getQueryParamValue(query, 'pickupDateInMs', param =>
          param.condition === '>=' ? param.value : undefined,
        ).find(isDefined),
        end: getQueryParamValue(query, 'pickupDateInMs', param =>
          param.condition === '<=' ? param.value : undefined,
        ).find(isDefined),
      };

      const params = {
        // Projections
        local_details: 'true',
        location_details: 'true',
        job_details: 'true',

        // Filters
        end_location_administrative_areas: getQueryParamValue(
          query,
          'endLocationAdministrativeAreas',
        )[0],
        end_operating_area_ids: query?.searchQueryParams.endOperatingAreaIds,
        jobs: getQueryParamValue(query, 'skillIds')[0],
        offset: query?.searchQueryParams.offset,
        query: query?.searchQueryParams.query || query?.searchQueryParams.q,
        start_location_administrative_areas: getQueryParamValue(
          query,
          'startLocationAdministrativeAreas',
        )[0],
        start_operating_area_ids:
          query?.searchQueryParams.startOperatingAreaIds,
        reverse_sort:
          isArray(order) && order[0]
            ? order[0].direction === OrderByDirection.ASC || undefined
            : undefined,
        sort_field:
          isArray(order) && order[0]
            ? convertSortFieldApi(order[0].field)
            : undefined,
        max_width: query?.searchQueryParams.maxWidth,
        max_length: query?.searchQueryParams.maxLength,
        min_weight: query?.searchQueryParams.minWeight,
        max_weight: query?.searchQueryParams.maxWeight,
        min_work_start_date: pickupDateRange.begin
          ? pickupDateRange.begin / 1000
          : undefined,
        max_work_start_date: pickupDateRange.end
          ? pickupDateRange.end / 1000
          : undefined,
        project_ids: ids,
        user_has_bid: query?.searchQueryParams.userHasBid,
        top_right_latitude: query?.searchQueryParams.topRightLatitude,
        top_right_longitude: query?.searchQueryParams.topRightLongitude,
        bottom_left_latitude: query?.searchQueryParams.bottomLeftLatitude,
        bottom_left_longitude: query?.searchQueryParams.bottomLeftLongitude,
      };

      return {
        endpoint: `projects/0.1/projects/active`,
        params,
      };
    },
    push: undefined,
    set: undefined,
    update: undefined,
    remove: undefined,
  };
}

function convertSortFieldApi(sortField: string): string | undefined {
  switch (sortField) {
    case 'timeUpdated':
      return 'submitdate';
    default:
      return undefined;
  }
}
